import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Info } from "lucide-react";
import { computeWorkforce } from "@/lib/workforceStats";
import { WorkforceSection } from "./WorkforceSection";
import { TenureSection } from "./TenureSection";
import { HiringSection } from "./HiringSection";
import { MilestonesSection } from "./MilestonesSection";

const UNASSIGNED = "__none__";

export const WorkforceAnalytics = ({ users, depts }) => {
    const navigate = useNavigate();
    const [dept, setDept] = useState("all");
    const options = useMemo(() => {
        const ids = new Set(users.map((u) => u.department_id || UNASSIGNED));
        const list = depts.filter((d) => ids.has(d.id)).sort((a, b) => a.name.localeCompare(b.name)).map((d) => [d.id, d.name]);
        if (ids.has(UNASSIGNED)) list.push([UNASSIGNED, "Unassigned"]);
        return list;
    }, [users, depts]);
    const list = useMemo(() => (dept === "all" ? users : users.filter((u) => (u.department_id || UNASSIGNED) === dept)), [users, dept]);
    const s = useMemo(() => computeWorkforce(list), [list]);
    const onOpen = (id) => navigate(`/employees/${id}`);

    return (
        <div className="space-y-10" data-testid="workforce-analytics">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg shadow-sm px-4 py-3">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 className="h-4 w-4 text-[#2EB4AC]" />
                    <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="wf-dept-filter"
                        className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-900 text-sm outline-none focus:ring-2 focus:ring-[#2EB4AC]">
                        <option value="all">All departments</option>
                        {options.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    </select>
                </label>
                {s.workforce.missingDoj > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="wf-missing-doj">
                        <Info className="h-3.5 w-3.5" />{s.workforce.missingDoj} of {s.workforce.counts.total} people have no joining date, so they're left out of tenure, hiring and milestone figures
                    </div>
                )}
            </div>
            <WorkforceSection w={s.workforce} />
            <TenureSection t={s.tenure} />
            <HiringSection h={s.hiring} byYear={s.workforce.byYear} />
            <MilestonesSection m={s.milestones} today={s.today} onOpen={onOpen} />
        </div>
    );
};
